import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutLabelTextGen } from '../layoutLabelText';
import { OptsLabelText } from '../optsLabelText';
import { GUtil } from '../../pdf/gutil';
import { IItemCount } from '@educraft/base';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';

/**
 * WordBuilder - Word as letter tiles
 *
 * Layout: Multiple portrait pages, 5×1 grid per page (5 rows, 1 column).
 * Each card: Image on left, word shown as individual letter tiles on the right.
 * Each letter of the word is displayed in its own colored square tile.
 *
 * Creative direction: Word as building blocks - each letter is a tangible tile.
 */
export class WordBuilder implements ILayoutLabelTextGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabelText): Promise<void> {
    const itemsPerPage = 5;
    const pagesNeeded = Math.ceil(opts.list.length / itemsPerPage);
    const pages: PageGen[] = [];
    for (let i = 0; i < pagesNeeded; i++) {
      pages.push(pdf.addPage());
    }

    const margin = PdfUnit.FromMillimeters(5);
    const cellWidth = PdfSize.A4.width - 2 * margin;
    const cellHeight = PdfSize.A4.height / 5;

    const vividPalettes: string[][] = [
      [
        '#FF1744',
        '#FF6D00',
        '#FFD600',
        '#00E676',
        '#00B0FF',
        '#651FFF',
        '#D500F9',
        '#F50057',
        '#00BFA5',
        '#FF9100',
      ],
      [
        '#FF4081',
        '#FFAB40',
        '#FFFF00',
        '#69F0AE',
        '#40C4FF',
        '#7C4DFF',
        '#E040FB',
        '#FF5252',
        '#18FFFF',
        '#FF6E40',
      ],
      [
        '#F44336',
        '#FF9800',
        '#FFEB3B',
        '#4CAF50',
        '#2196F3',
        '#3F51B5',
        '#9C27B0',
        '#E91E63',
        '#00BCD4',
        '#FF5722',
      ],
    ];

    const tileColors = GUtil.randomBrushes(opts.data.random, vividPalettes);

    for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
      const page = pages[pageIndex];
      const pageStartIndex = pageIndex * itemsPerPage;

      for (let y = 0; y < 5; y++) {
        const index = pageStartIndex + y;
        if (index >= opts.list.length) continue;

        const localIndex = y;
        const rect = new PdfRect(margin, y * cellHeight, cellWidth, cellHeight);

        const imageAreaWidth = rect.width * 0.35;
        const wordAreaWidth = rect.width * 0.65;

        const imageRect = new PdfRect(rect.x, rect.y, imageAreaWidth, rect.height);
        const wordAreaRect = new PdfRect(
          rect.x + imageAreaWidth,
          rect.y,
          wordAreaWidth,
          rect.height
        );

        page.drawLabel(opts.list[index].label, { rect: imageRect });

        const word = opts.list[index].text.toUpperCase().replace(/\s/g, '');
        const letters = word.split('');
        if (letters.length === 0) continue;

        const tilePadding = PdfUnit.FromMillimeters(4);
        const tileWidth =
          (wordAreaRect.width - tilePadding * (letters.length + 1)) / letters.length;
        const tileHeight = wordAreaRect.height - tilePadding * 2;
        const tileSize = Math.min(tileWidth, tileHeight);

        const totalTilesWidth = letters.length * tileSize + (letters.length - 1) * tilePadding;
        const startX = wordAreaRect.x + (wordAreaRect.width - totalTilesWidth) / 2;
        const startY = wordAreaRect.y + (wordAreaRect.height - tileSize) / 2;

        const fontSize = tileSize * 1.1;
        const letterFont = new PdfFont(PdfFont.BOLD_FONT_NAME, fontSize);

        for (let li = 0; li < letters.length; li++) {
          const tileX = startX + li * (tileSize + tilePadding);
          const tileRect = new PdfRect(tileX, startY, tileSize, tileSize);
          const tileColorIndex = (localIndex + li) % tileColors.length;

          page.drawRectangle(null, tileColors[tileColorIndex], tileRect);
          page.drawString(
            letters[li],
            letterFont,
            PdfBrush.White,
            null,
            tileRect,
            ETextAlign.Center
          );
        }

        page.drawRectangle(GUtil.cutPen, null, rect);
      }
    }
  }
}
